import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import {
    DisableTextSendProvider,
    InputModeProvider,
    CustomTextInputProvider,
} from './hooks';
import { AzureAD } from 'react-aad-msal';
import { getAuthProvider } from './authProvider';
import { getInitValuesFromLoader, getTenantName } from './helpers';
import { Config, fetchConfig, needAAD } from './config';
import { ConfigProvider } from './contextProviders';
import { initAppInsights } from './logging';
import './variables.css';
import './style.css';
import {
    getAnalyticsUserId,
    getBaseUserProperties,
    initializeAnalyticsEvents,
} from './analytics';
import { amplitudeClients } from './amplitude';
import {
    genesysAuthRedirectIfTokenRedirectSet,
    ifGenesysRedirectSaveAuthCodeToWindow,
    setHealthSystemInformationToGenesysAuth,
} from './genesysService';
import { MESSAGE_ASSIST_MESSAGE_EVENT_TYPES, TENANTS } from './constants';
import {
    getMessageAssistAnalyticId,
    postMessageAssistMessage,
} from './messageassist';
import { getHealthSystemAccessToken } from './authentication';
import { InitLoaderValues } from './types';

(async () => {
    let config: Config;

    try {
        config = await fetchConfig();
    } catch (e: any) {
        if (getTenantName() == TENANTS.MESSAGE_ASSIST) {
            postMessageAssistMessage(
                MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.ON_GRACE_LOAD_FAIL
            );
        }

        throw e;
    }

    initAppInsights(config.appInsightsKey, config.tier);
    // TODO: consider parallelizing below calls

    let initLoaderValues: InitLoaderValues | null;

    if (getTenantName() == TENANTS.MESSAGE_ASSIST) {
        // For Message Assist, we don't post a message to the loader
        // to get these initial values, so they have to come from MA
        initLoaderValues = {
            amplitudeUserId: getMessageAssistAnalyticId(),
        };
    } else {
        initLoaderValues = await getInitValuesFromLoader(
            config.validTokenOriginList
        );
    }

    window.genesysAuth = {
        authCode: initLoaderValues?.gac || '',
        codeVerifier: initLoaderValues?.gcv || '',
        redirectUri: initLoaderValues?.gru || '',
    };

    if (await genesysAuthRedirectIfTokenRedirectSet(config)) {
        return;
    }

    ifGenesysRedirectSaveAuthCodeToWindow();

    // Attempt to get from cookie first
    const healthSystemToken =
        await getHealthSystemAccessToken(initLoaderValues);
    const amplitudeUserId = getAnalyticsUserId(
        initLoaderValues,
        healthSystemToken
    );

    setHealthSystemInformationToGenesysAuth(healthSystemToken);

    const baseUserProperties = getBaseUserProperties(
        initLoaderValues,
        config.tier,
        config.brandParentUrl
    );

    const analyticsEventReportingAPIKey = config.amplitudeAnalyticsKey;
    try {
        await Promise.all([
            amplitudeClients.initializeExperimentClient(
                config.amplitudeDeploymentKey,
                config.amplitudeReverseProxyEndpoint,
                amplitudeUserId,
                baseUserProperties
            ),
            initializeAnalyticsEvents(
                baseUserProperties,
                analyticsEventReportingAPIKey,
                amplitudeUserId,
                config.amplitudeReverseProxyEndpoint
            ),
        ]);
    } catch (e) {
        if (getTenantName() == TENANTS.MESSAGE_ASSIST) {
            postMessageAssistMessage(
                MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.ON_GRACE_LOAD_FAIL
            );
            return;
        }
    }

    // https://www.npmjs.com/package/react-aad-msal
    if (needAAD(config.tier, config.msalConfig)) {
        const authProvider = getAuthProvider(config.msalConfig);
        ReactDOM.render(
            <React.StrictMode>
                {/* @ts-ignore */}
                <AzureAD provider={authProvider} forceLogin={true}>
                    <ConfigProvider config={config}>
                        <InputModeProvider initialMode="disabled">
                            <CustomTextInputProvider initialMode="default">
                                <DisableTextSendProvider initialMode={false}>
                                    <App
                                        healthSystemToken={healthSystemToken}
                                        initLoaderValues={initLoaderValues}
                                    />
                                </DisableTextSendProvider>
                            </CustomTextInputProvider>
                        </InputModeProvider>
                    </ConfigProvider>
                </AzureAD>
            </React.StrictMode>,
            document.getElementById('webchat')
        );
    } else {
        ReactDOM.render(
            <React.StrictMode>
                <ConfigProvider config={config}>
                    <InputModeProvider initialMode="disabled">
                        <CustomTextInputProvider initialMode="default">
                            <DisableTextSendProvider initialMode={false}>
                                <App
                                    healthSystemToken={healthSystemToken}
                                    initLoaderValues={initLoaderValues}
                                />
                            </DisableTextSendProvider>
                        </CustomTextInputProvider>
                    </InputModeProvider>
                </ConfigProvider>
            </React.StrictMode>,
            document.getElementById('webchat')
        );
    }
})();
